import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {Action, RemoteResponse, RemoteStatus} from '../../../domain/infra/remote';
import {ChargeRecordDto} from '../../../domain/chargeRecord/chargeRecordDto';
import {ChargeRecordPowerDto} from '../../../domain/chargeRecord/chargeRecordPowerDto';
import {ChargeRecordStatus} from '../../../domain/chargeRecord/chargeRecord';
import {FrontIziviaTranslateService} from '../../../services/i18n/iziviaTranslate.service';
import {IziviaLoggerService} from '../../../services/common/iziviaLogger.service';
import {PriceDto} from '../../../domain/priceDto';
import moment, {now} from 'moment';
import {Subscription, timer} from 'rxjs';
import {ChargeRecordService} from '../../../services/business/chargeRecord.service';
import {GhostComponent} from '../../../utils/ghostComponent';
import {AlertControllerService} from '../../../services/common/alertController.service';
import {ChargeRecordDetailsDuration} from '../../../domain/chargeRecord/chargeRecordDetailsDuration';
import {RemoteInfoChargeRecord} from '../../../domain/chargeRecord/remoteInfoChargeRecord';
import {IziviaToastService} from '../../../services/common/iziviaToast.service';

@Component({
    selector: 'charge-record-details',
    templateUrl: './chargeRecordDetails.component.html',
    styleUrls: ['./chargeRecordDetails.component.scss']
})
export class ChargeRecordDetailsComponent extends GhostComponent implements OnInit, OnChanges, OnDestroy {
    @Input() set chargeRecord(chargeRecord: ChargeRecordDto) {
        this._chargeRecord = chargeRecord;
        if (chargeRecord && chargeRecord.id) {
            this.createPricePolling(chargeRecord);
            this.createPowerPolling(chargeRecord);
        }
    }
    @Input() pollingTimerInSecond: number;
    @Output() remoteStoppedEvent: EventEmitter<void> = new EventEmitter<void>();


    get chargeRecord(): ChargeRecordDto {
        return this._chargeRecord;
    }

    // dirty hack to enable type-safety to the view
    private _chargeRecord: ChargeRecordDto;
    ChargeRecordStatus = ChargeRecordStatus;
    Action = Action;
    chargeRecordDetailsDuration: ChargeRecordDetailsDuration;

    chargeRecordPower: ChargeRecordPowerDto;
    consumptionPrice: PriceDto;
    durationTimerSubscription: Subscription;
    remoteRequest: RemoteInfoChargeRecord;
    private powerSubscription: Subscription;
    private priceSubscription: Subscription;

    constructor(
        private readonly alertControllerService: AlertControllerService,
        private readonly chargeRecordService: ChargeRecordService,
        private readonly loggerService: IziviaLoggerService,
        private readonly toastService: IziviaToastService,
        private readonly translateService: FrontIziviaTranslateService) {
        super();
    }

    public ngOnInit(): void {
        this.resetChargeRecordDuration();
        this.durationTimerSubscription = timer(0, 60_000).subscribe(() => this.calculateDuration(this.chargeRecord));
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!!changes && !!changes.chargeRecord && !!changes.chargeRecord.currentValue) {
            this.calculateDuration(changes.chargeRecord.currentValue);
        } else {
            this.resetChargeRecordDuration();
        }
    }

    private calculateDuration(chargeRecord: ChargeRecordDto): void {
        this.resetChargeRecordDuration();
        if (!chargeRecord || !chargeRecord.startDate) {
            this.resetChargeRecordDuration();
            return;
        }

        const duration = moment.duration(moment(now()).diff(moment(this.chargeRecord.startDate)));

        this.chargeRecordDetailsDuration.hours = Math.trunc(duration.asHours());
        this.chargeRecordDetailsDuration.minutes = duration.minutes();
    }

    presentRemoteStop() {
        if (!this.chargeRecord || this.chargeRecord.status !== ChargeRecordStatus.CHARGING || !this.chargeRecord.remoteStop) {
            return;
        }

        this.alertControllerService.display({
            header: this.translateService.translate('chargeRecord.remote.stop.modal.header'),
            message: this.translateService.translate('chargeRecord.remote.stop.modal.message'),
            buttons: [
                {
                    text: this.translateService.translate('chargeRecord.remote.stop.modal.button.cancel'),
                    role: 'cancel'
                },
                {
                    text: this.translateService.translate('chargeRecord.remote.stop.modal.button.ok'),
                    handler: () => {
                        this.remoteRequest = new RemoteInfoChargeRecord(this.chargeRecord.id);
                    }
                }
            ]
        }).subscribe();
    }

    handleRemoteStopResponse(response: RemoteResponse) {
        if (response?.status === RemoteStatus.DONE) {
            this.chargeRecord.status = ChargeRecordStatus.STOPPED;
            this.remoteStoppedEvent.emit();
        } else if (response?.status === RemoteStatus.TIMEOUT) {
            this.loggerService.debug('[handleRemoteResponse] TIMOUT', response);
            this.remoteStoppedEvent.emit();
        } else {
            this.remoteRequest = null;
        }
    }

    ngOnDestroy(): void {
        if (this.durationTimerSubscription) {
            this.durationTimerSubscription.unsubscribe();
        }
        if (this.priceSubscription) {
            this.priceSubscription.unsubscribe();
        }
        if (this.powerSubscription) {
            this.powerSubscription.unsubscribe();
        }
    }

    generateGhost(): void {
    }

    private createPricePolling(chargeRecord: ChargeRecordDto) {
        if (!!this.priceSubscription) {
            this.priceSubscription.unsubscribe();
        }
        this.priceSubscription = this.chargeRecordService.loadPrice(chargeRecord.id, this.pollingTimerInSecond)
            .subscribe(
                consumptionPrice => {
                    this.consumptionPrice = consumptionPrice;
                },
                error => {
                    this.consumptionPrice = null;
                    this.toastService.showError('charge.price.calculationError');
                }
            );
    }

    private createPowerPolling(chargeRecord: ChargeRecordDto) {
        if (!!this.powerSubscription) {
            this.powerSubscription.unsubscribe();
        }
        this.powerSubscription = this.chargeRecordService.loadPower(chargeRecord.id, this.pollingTimerInSecond)
            .subscribe(chargeRecordPower => {
                this.chargeRecordPower = chargeRecordPower;
            });
    }

    private resetChargeRecordDuration() {
        this.chargeRecordDetailsDuration = {hours: 0, minutes: 0};
    }
}

